/**
 * Los desplegables de «Catálogo de cirugías, tratamientos y otros costes»
 * (administración, 6/10/2026). Desde el 28/10/2026 (administración), la
 * reorganización por médicos:
 *
 *  - ARRIBA A LA DERECHA, siempre visibles (admin): «Añadir cirugía» y
 *    «Añadir tratamiento». Se rellena nombre, precio, Con/Sin IVA, anestesia,
 *    duración opcional y nota, y se marca a qué médico(s) o bloque(s) va:
 *    una fila por cada uno (luego cada médico personaliza su nota y su
 *    tiempo). Sin bloques todavía, van a la lista anterior.
 *  - NIVEL 1, los BLOQUES PRINCIPALES (uno por desplegable, con el nombre
 *    que les pone quien los crea: no hay ninguno en el código). Los crea,
 *    renombra y da de baja solo quien tiene en Usuarios «Gestiona los
 *    bloques del Catálogo» (lo dice el servidor, `gestiona_bloques`); el
 *    resto de botones los ve administración. Un bloque puede ser una lista
 *    única (sus filas cuelgan de él) o llevar NIVEL 2, un subapartado por
 *    MÉDICO (enlazable con su ficha), cada uno con sus cirugías y sus
 *    tratamientos: precio, IVA, anestesia, duración y nota por fila.
 *  - En el lápiz de cada cirugía o tratamiento, «Añadir a otro médico» la
 *    copia con todos sus campos; «Dónde» la mueve.
 *  - LA LISTA ANTERIOR: TRATAMIENTOS y CIRUGÍAS (sueltos y por apartados,
 *    como hasta ahora) se quedan tal cual hasta que alguien los reparte a
 *    mano con «Mover a…» (cada fila) o «Mover todas a…» (cada apartado).
 *    Nada se asigna solo. En TRATAMIENTOS sigue el bloque editable
 *    «Tratamientos de colaboradores» (27/10/2026, el tarifario de
 *    Colaboradores por sus rutas); en CIRUGÍAS, el de solo lectura.
 *  - HOSPITALES — Tarifas y ALQUILER QUIRÓFANO EXTERNOS, como estaban.
 *
 * Lo ve todo el que entra a la pantalla; escribe administración (y los
 * bloques, la casilla). El servidor lo impone (`routers/catalogo_precios_router.py`)
 * y lo apunta en Control rastreo. Aquí no hay ni un precio ni un nombre:
 * todo viene de `GET /api/catalogo-precios` (y las fichas, de `/api/surgeons`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, ChevronRight, ClipboardPaste, FolderPlus, LayoutList, Pencil, Plus, Trash2, UserPlus,
} from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import {
  SECCIONES_PARTIDAS, catalogoVacio, conArticulo, cuentaBloque, cuentaPartidas, destinosDe, esFemenino,
  filasAnteriores, normalizarCatalogo, textoImporte, textoIva,
} from '../../lib/catalogoPrecios';
import { Button } from '../ui/button';
import AlquilerQuirofanoExternos, { BLOQUE_EXTERNOS } from './AlquilerQuirofanoExternos';
import ElementoPrecioDialog from './ElementoPrecioDialog';
import ImportarPreciosDialog from './ImportarPreciosDialog';
import TratamientoColaboradorDialog, { RUTA_TRATAMIENTOS_COLAB } from './TratamientoColaboradorDialog';

const SISTEMA_VACIO = { tratamientos_colaboradores: [], cirugias_colaboradores: [] };
const lista = (v) => (Array.isArray(v) ? v : []);

/** Un desplegable: la cabecera es un botón que lo abre y lo cierra. */
function Desplegable({ titulo, resumen, abierto, onToggle, testid, children, principal = false }) {
  return (
    <section className={principal ? 'cjo-card mt-4' : 'border border-border rounded-md mt-3'} data-testid={testid}>
      <button type="button" onClick={onToggle} aria-expanded={abierto} data-testid={`${testid}-abrir`}
        className={`w-full flex items-center justify-between gap-3 text-left ${principal ? 'p-4' : 'px-3 py-2.5'}`}>
        <span className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`w-4 h-4 shrink-0 text-[#2E6E5E] transition-transform ${abierto ? 'rotate-90' : ''}`} />
          <span className={principal ? 'font-semibold tracking-wide text-[#13231E]' : 'font-medium text-[#13231E] min-w-0'}>
            {titulo}
          </span>
        </span>
        {resumen && <span className="text-xs text-muted-foreground text-right shrink-0">{resumen}</span>}
      </button>
      {abierto && <div className={principal ? 'px-4 pb-4' : 'px-3 pb-3'}>{children}</div>}
    </section>
  );
}

/** Subir, bajar, editar y dar de baja una fila (solo administración).
 *  `sinOrden`: sin subir ni bajar (el tarifario de colaboradores va por
 *  nombre, no tiene orden propio). `sinEdicion`: sin lápiz ni baja (un
 *  bloque, para quien no tiene la casilla). */
function Acciones({ hermanos, indice, onMover, onEditar, onBaja, etiqueta, sinOrden = false, sinEdicion = false }) {
  return (
    <div className="flex gap-0.5 justify-end">
      {!sinOrden && (
        <>
          <Button type="button" size="sm" variant="ghost" disabled={indice === 0}
            onClick={() => onMover(hermanos, indice, -1)} title="Subir" aria-label={`Subir ${etiqueta}`}
            data-testid="catalogo-subir"><ArrowUp className="w-3.5 h-3.5" /></Button>
          <Button type="button" size="sm" variant="ghost" disabled={indice === hermanos.length - 1}
            onClick={() => onMover(hermanos, indice, 1)} title="Bajar" aria-label={`Bajar ${etiqueta}`}
            data-testid="catalogo-bajar"><ArrowDown className="w-3.5 h-3.5" /></Button>
        </>
      )}
      {!sinEdicion && (
        <>
          <Button type="button" size="sm" variant="ghost" onClick={onEditar} title="Editar"
            aria-label={`Editar ${etiqueta}`} data-testid="catalogo-editar"><Pencil className="w-3.5 h-3.5" /></Button>
          <Button type="button" size="sm" variant="ghost" onClick={onBaja} title="Dar de baja"
            aria-label={`Dar de baja ${etiqueta}`} className="text-destructive"
            data-testid="catalogo-baja"><Trash2 className="w-3.5 h-3.5" /></Button>
        </>
      )}
    </div>
  );
}

function Nota({ texto, testid, className = '' }) {
  if (!texto) return null;
  return <p className={`text-sm whitespace-pre-line ${className}`} data-testid={testid}>{texto}</p>;
}

/** «Reglas de cálculo»: informativas, plegadas bajo su título. */
function Reglas({ texto }) {
  const [abierto, setAbierto] = useState(false);
  if (!texto) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setAbierto((a) => !a)} aria-expanded={abierto}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#2E6E5E] hover:underline"
        data-testid="catalogo-apartado-reglas-abrir">
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${abierto ? 'rotate-90' : ''}`} />
        Reglas de cálculo
      </button>
      {abierto && (
        <div className="mt-1.5 rounded-md bg-[rgba(46,110,94,0.05)] border border-border px-3 py-2 text-xs whitespace-pre-line"
          data-testid="catalogo-apartado-reglas">{texto}</div>
      )}
    </div>
  );
}

/** «1 forfait», «3 forfaits», «0 grupos de servicios» (6/10/2026: salía «1 forfaits»). */
function cuantos(n, uno, varios) {
  return `${n} ${n === 1 ? uno : varios}`;
}

function Botonera({ children }) {
  return <div className="flex flex-wrap gap-2 my-2">{children}</div>;
}

function BotonAnadir({ onClick, children, testid, icono: Icono = Plus }) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onClick} data-testid={testid}>
      <Icono className="w-3.5 h-3.5 mr-1.5" />{children}
    </Button>
  );
}

/** «Mover a…»: el desplegable con los médicos y bloques (28/10/2026). */
function MoverA({ destinos, onMover, etiqueta, testid }) {
  if (!destinos.length) return null;
  return (
    <select value="" onChange={(e) => { if (e.target.value) onMover(e.target.value); }}
      aria-label={`Mover ${etiqueta} a`} title="Mover a un médico o a un bloque"
      className="h-8 max-w-[11rem] px-2 rounded-md border border-input bg-background text-xs"
      data-testid={testid}>
      <option value="">{etiqueta === 'todas' ? 'Mover todas a…' : 'Mover a…'}</option>
      {destinos.map((d) => <option key={d.id} value={d.id}>{d.etiqueta}</option>)}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Partidas: la tabla de tratamientos o de cirugías de un sitio
// ---------------------------------------------------------------------------

/**
 * @param moverA  los destinos de «Mover a…» por fila (solo en la lista
 *                anterior y para admin); null en los bloques y los médicos.
 */
function TablaPartidas({ filas, seccion, admin, acc, testid, moverA = null }) {
  const conf = SECCIONES_PARTIDAS[seccion];
  if (!filas.length) return null;
  // Anestesia y duración solo salen si alguna fila las lleva: vacías no se
  // pintan (28/10/2026).
  const conAnestesia = filas.some((f) => f.anestesia);
  const conDuracion = filas.some((f) => f.duracion);
  const ancho = admin ? 'min-w-[820px]' : 'min-w-[600px]';
  return (
    <div className="overflow-x-auto">
      {/* Anchos fijos: las tablas de los apartados quedan alineadas entre sí. */}
      <table className={`w-full text-sm table-fixed ${ancho}`} data-testid={testid}>
        <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-3 font-semibold">Nombre</th>
            <th className="py-2 px-3 font-semibold text-right w-36">{conf.precio}</th>
            <th className="py-2 px-3 font-semibold w-20">IVA</th>
            {conAnestesia && <th className="py-2 px-3 font-semibold w-32">Anestesia</th>}
            {conDuracion && <th className="py-2 px-3 font-semibold w-24">Duración</th>}
            <th className="py-2 px-3 font-semibold w-[28%]">Nota</th>
            {admin && <th className={`py-2 pl-3 ${moverA ? 'w-72' : 'w-44'}`} />}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={f.id} className="border-b border-border/60 align-top" data-testid="catalogo-partida">
              <td className="py-2 pr-3 font-medium text-[#13231E]">{f.nombre}</td>
              <td className="py-2 px-3 text-right tabular-nums font-semibold whitespace-nowrap">
                {textoImporte(f.precio, f.precio_matiz)}
              </td>
              <td className={`py-2 px-3 text-xs whitespace-nowrap ${f.sin_iva ? 'font-medium text-[#13231E]' : 'text-muted-foreground'}`}
                data-testid="catalogo-partida-iva">{textoIva(f.sin_iva)}</td>
              {conAnestesia && <td className="py-2 px-3 text-xs" data-testid="catalogo-partida-anestesia">{f.anestesia || ''}</td>}
              {conDuracion && <td className="py-2 px-3 text-xs" data-testid="catalogo-partida-duracion">{f.duracion || ''}</td>}
              <td className="py-2 px-3 text-xs text-muted-foreground whitespace-pre-line">{f.nota || ''}</td>
              {admin && (
                <td className="py-1 pl-3">
                  <div className="flex items-center justify-end gap-1">
                    {moverA && (
                      <MoverA destinos={moverA} etiqueta={f.nombre} testid="catalogo-mover-a"
                        onMover={(padreId) => acc.moverA(conf.fila, f, padreId)} />
                    )}
                    <Acciones hermanos={filas} indice={i} etiqueta={f.nombre} onMover={acc.mover}
                      onEditar={() => acc.editar(conf.fila, f)} onBaja={() => acc.baja(conf.fila, f)} />
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// La lista anterior: TRATAMIENTOS y CIRUGÍAS, sueltos y por apartados
// ---------------------------------------------------------------------------

function BloqueApartado({ apartado, hermanos, indice, seccion, admin, acc, destinos, nivel = 1 }) {
  const conf = SECCIONES_PARTIDAS[seccion];
  const vacio = !apartado.filas.length && !(apartado.subapartados || []).length;
  return (
    <div className={nivel === 1 ? 'mt-4 rounded-md border border-border p-3' : 'mt-3 pl-3 border-l-2 border-[rgba(46,110,94,0.25)]'}
      data-testid="catalogo-apartado" data-nivel={nivel}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className={nivel === 1 ? 'text-base font-semibold text-[#13231E]' : 'text-sm font-semibold text-[#1E4A3F]'}>
          {apartado.nombre}
        </h3>
        {admin && (
          <div className="flex items-center gap-1">
            {apartado.filas.length > 0 && (
              <MoverA destinos={destinos} etiqueta="todas" testid="catalogo-mover-todas"
                onMover={(padreId) => acc.moverTodas(apartado.filas, padreId, apartado.nombre)} />
            )}
            <Acciones hermanos={hermanos} indice={indice} etiqueta={apartado.nombre} onMover={acc.mover}
              onEditar={() => acc.editar('apartado', apartado)} onBaja={() => acc.baja('apartado', apartado)} />
          </div>
        )}
      </div>
      <Nota texto={apartado.nota} testid="catalogo-apartado-nota"
        className="mt-1 rounded bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5" />
      <Reglas texto={apartado.reglas} />
      {admin && (
        <Botonera>
          <BotonAnadir onClick={() => acc.nuevo(conf.fila, apartado.id, null, apartado.nombre)}
            testid="catalogo-apartado-nuevo">{conf.una === 'cirugía' ? 'Cirugía' : 'Tratamiento'}</BotonAnadir>
          {nivel === 1 && (
            <BotonAnadir onClick={() => acc.nuevo('apartado', apartado.id, seccion, apartado.nombre)}
              testid="catalogo-apartado-nuevo-sub" icono={FolderPlus}>Subapartado</BotonAnadir>
          )}
          <BotonAnadir onClick={() => acc.importar(conf.fila, apartado.id, apartado.nombre)}
            testid="catalogo-apartado-importar" icono={ClipboardPaste}>Importar</BotonAnadir>
        </Botonera>
      )}
      <TablaPartidas filas={apartado.filas} seccion={seccion} admin={admin} acc={acc} testid="catalogo-partidas"
        moverA={admin && destinos.length ? destinos : null} />
      {(apartado.subapartados || []).map((s, i) => (
        <BloqueApartado key={s.id} apartado={s} hermanos={apartado.subapartados} indice={i}
          seccion={seccion} admin={admin} acc={acc} destinos={destinos} nivel={2} />
      ))}
      {vacio && <p className="text-xs text-muted-foreground mt-1">Sin filas todavía.</p>}
    </div>
  );
}

/** Lo que ya estaba en el sistema: se ve aquí, se cambia en su sitio. */
function SoloLectura({ titulo, explicacion, columnas, filas, testid }) {
  if (!filas.length) return null;
  return (
    <div className="mt-5 rounded-md border border-dashed border-border p-3" data-testid={testid}>
      <h4 className="text-sm font-semibold text-[#13231E]">
        {titulo} <span className="ml-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-[rgba(46,110,94,0.1)] text-[#2E6E5E]">Solo lectura</span>
      </h4>
      <p className="text-xs text-muted-foreground mt-0.5 mb-2">{explicacion}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              {columnas.map((c, i) => (
                <th key={c} className={`py-1.5 ${i ? 'px-3 text-right' : 'pr-3'} font-semibold`}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((celdas) => (
              <tr key={celdas.key} className="border-b border-border/60" data-testid="catalogo-sistema-fila">
                {celdas.valores.map((v, i) => (
                  <td key={i} className={`py-1.5 ${i ? 'px-3 text-right tabular-nums whitespace-nowrap' : 'pr-3'}`}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * «Tratamientos de colaboradores», editable (administración, 27/10/2026): el
 * tarifario con el que se factura a los médicos colaboradores, con la misma
 * pinta que el resto de bloques del catálogo. Sin «Importar»: el pegado del
 * catálogo de precios escribe en `catalogo_precios`, no en el tarifario.
 */
function TratamientosColaboradores({ filas, acc }) {
  return (
    <div className="mt-5 rounded-md border border-border p-3" data-testid="catalogo-colab-tratamientos">
      <h4 className="text-sm font-semibold text-[#13231E]">Tratamientos de colaboradores</h4>
      <p className="text-xs text-muted-foreground mt-0.5">
        El tarifario con el que se calcula la facturación de los médicos colaboradores: es el mismo que
        Colaboradores → Tarifario. Un cambio vale para los actos que se registren desde ahora; lo ya
        registrado conserva su importe.
      </p>
      <Botonera>
        <BotonAnadir onClick={acc.colabNuevo} testid="catalogo-colab-nuevo">Tratamiento</BotonAnadir>
      </Botonera>
      {filas.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="catalogo-colab-vacio">
          Aún no hay tratamientos de colaboradores: añade el primero.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed min-w-[740px]" data-testid="catalogo-colab-tabla">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-semibold">Nombre</th>
                <th className="py-2 px-3 font-semibold text-right w-40">PVP</th>
                <th className="py-2 px-3 font-semibold w-24">IVA</th>
                <th className="py-2 px-3 font-semibold w-[30%]">Nota</th>
                <th className="py-2 pl-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {filas.map((t) => (
                <tr key={t.id} className="border-b border-border/60 align-top" data-testid="catalogo-colab-fila">
                  <td className="py-2 pr-3 font-medium text-[#13231E]">{t.nombre}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold whitespace-nowrap">{textoImporte(t.pvp)}</td>
                  <td className="py-2 px-3 text-xs" data-testid="catalogo-colab-iva">{textoIva(t.sin_iva)}</td>
                  <td className="py-2 px-3 text-xs text-muted-foreground whitespace-pre-line">{t.nota || ''}</td>
                  <td className="py-1 pl-3">
                    <Acciones sinOrden etiqueta={t.nombre} onEditar={() => acc.colabEditar(t)}
                      onBaja={() => acc.colabBaja(t)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SeccionPartidas({ seccion, datos, admin, gestion, acc, sistema, destinos }) {
  const conf = SECCIONES_PARTIDAS[seccion];
  const vacia = !datos.filas.length && !datos.apartados.length;
  const anteriores = filasAnteriores(datos);
  // Gestión (la que puede cambiarlo) va directa al tarifario de Colaboradores,
  // que se abre solo con ?tarifario=… (7/10/2026), en la pestaña de lo que se
  // está viendo: tratamientos (?tarifario=1) o cirugías (?tarifario=cirugias,
  // revisión del 7/10/2026). El resto solo lo ve.
  const dondeSeGestiona = (pestana) => (gestion ? (
    <>{' '}<Link to={`/colaboradores?tarifario=${pestana}`} className="underline text-[#2E6E5E] font-medium"
      data-testid="catalogo-editar-colaboradores">Editar en Colaboradores</Link></>
  ) : ' Se gestiona en Colaboradores.');
  return (
    <div>
      {admin && (
        <Botonera>
          <BotonAnadir onClick={() => acc.nuevo(conf.fila, null, null, 'sin apartado')}
            testid={`catalogo-${seccion}-nuevo`}>{conf.una === 'cirugía' ? 'Cirugía' : 'Tratamiento'}</BotonAnadir>
          <BotonAnadir onClick={() => acc.nuevo('apartado', null, seccion, conf.titulo.toLowerCase())}
            testid={`catalogo-${seccion}-nuevo-apartado`} icono={FolderPlus}>Apartado</BotonAnadir>
          <BotonAnadir onClick={() => acc.importar(conf.fila, null, `${conf.titulo.toLowerCase()} sin apartado`)}
            testid={`catalogo-${seccion}-importar`} icono={ClipboardPaste}>Importar</BotonAnadir>
          {datos.filas.length > 0 && (
            <MoverA destinos={destinos} etiqueta="todas" testid="catalogo-mover-todas"
              onMover={(padreId) => acc.moverTodas(datos.filas, padreId, `${conf.titulo.toLowerCase()} sin apartado`)} />
          )}
        </Botonera>
      )}
      {/* La migración manual (28/10/2026): lo de aquí no está en ningún
          médico; se reparte a mano, nada se asigna solo. */}
      {admin && destinos.length > 0 && anteriores.length > 0 && (
        <p className="text-xs rounded bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5 mb-2"
          data-testid={`catalogo-${seccion}-lista-anterior`}>
          Lista anterior: {cuantos(anteriores.length, 'fila', 'filas')} sin asignar a ningún médico ni bloque.
          Se reparten a mano con «Mover a…» en cada fila, o «Mover todas a…» en cada apartado; no se asigna
          nada solo.
        </p>
      )}
      {/* Dos precios para la misma cirugía (revisión del 6/10/2026): estos son
          informativos; los que cobra la clínica salen de la tabla de
          intervenciones. Se dice aquí, discreto, para que nadie cambie uno
          creyendo que cambia el otro. */}
      {seccion === 'cirugias' && (
        <p className="text-xs text-muted-foreground mb-2" data-testid="catalogo-cirugias-aviso-precios">
          Precios informativos: el presupuesto y Cobros usan los de «Intervenciones y su consentimiento».
        </p>
      )}
      {vacia && (
        <p className="text-sm text-muted-foreground" data-testid={`catalogo-${seccion}-vacio`}>
          {/* «cirugías cargadas», «tratamientos cargados» (6/10/2026: concordaba mal). */}
          {admin ? `Aún no hay ${seccion === 'cirugias' ? 'cirugías' : 'tratamientos'}: añade la primera fila o impórtalas pegando una tabla.`
            : `Aún no hay ${seccion === 'cirugias' ? 'cirugías cargadas' : 'tratamientos cargados'}.`}
        </p>
      )}
      <TablaPartidas filas={datos.filas} seccion={seccion} admin={admin} acc={acc} testid="catalogo-partidas"
        moverA={admin && destinos.length ? destinos : null} />
      {datos.apartados.map((a, i) => (
        <BloqueApartado key={a.id} apartado={a} hermanos={datos.apartados} indice={i}
          seccion={seccion} admin={admin} acc={acc} destinos={destinos} />
      ))}

      {/* El tarifario de tratamientos de colaboradores: quien edita el
          catálogo lo edita aquí (27/10/2026); el resto lo ve de solo lectura
          y gestión sigue teniendo su enlace a Colaboradores → Tarifario. */}
      {seccion === 'tratamientos' && admin && (
        <TratamientosColaboradores filas={sistema.tratamientos_colaboradores} acc={acc} />
      )}
      {seccion === 'tratamientos' && !admin && (
        <SoloLectura
          testid="catalogo-sistema-tratamientos"
          titulo="Ya en el sistema: tratamientos de colaboradores"
          explicacion={<>El tarifario con el que se calcula la facturación de los médicos colaboradores. Aquí solo se ve, tal cual está.{dondeSeGestiona('1')}</>}
          columnas={['Tratamiento', 'PVP']}
          filas={sistema.tratamientos_colaboradores.map((t) => ({
            key: t.id || t.nombre,
            valores: [t.nombre, `${textoImporte(t.pvp)}${t.sin_iva ? ' (sin IVA)' : ''}`],
          }))}
        />
      )}
      {seccion === 'cirugias' && (
        <SoloLectura
          testid="catalogo-sistema-cirugias"
          titulo="Ya en el sistema: cirugías de colaboradores"
          explicacion={<>El tarifario de las cirugías de los cirujanos colaboradores. Aquí solo se ve.{dondeSeGestiona('cirugias')}</>}
          columnas={['Cirugía', 'Precio sugerido']}
          filas={sistema.cirugias_colaboradores.map((c) => ({
            key: c.id || c.nombre,
            valores: [c.nombre, `${textoImporte(c.sugerido)}${c.sin_iva ? ' (exenta de IVA)' : ''}`],
          }))}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NIVEL 1 y NIVEL 2: bloques principales y sus médicos (28/10/2026)
// ---------------------------------------------------------------------------

/** Las cirugías y los tratamientos de un sitio (un bloque o un médico). */
function PartidasDeSitio({ sitio, admin, acc, testidPrefix }) {
  const vacio = !sitio.cirugias.length && !sitio.tratamientos.length;
  return (
    <>
      {admin && (
        <Botonera>
          <BotonAnadir onClick={() => acc.nuevo('cirugia', sitio.id, null, sitio.nombre)}
            testid={`${testidPrefix}-nueva-cirugia`}>Cirugía</BotonAnadir>
          <BotonAnadir onClick={() => acc.nuevo('tratamiento', sitio.id, null, sitio.nombre)}
            testid={`${testidPrefix}-nuevo-tratamiento`}>Tratamiento</BotonAnadir>
          <BotonAnadir onClick={() => acc.importar('cirugia', sitio.id, `cirugías de ${sitio.nombre}`)}
            testid={`${testidPrefix}-importar-cirugias`} icono={ClipboardPaste}>Importar cirugías</BotonAnadir>
          <BotonAnadir onClick={() => acc.importar('tratamiento', sitio.id, `tratamientos de ${sitio.nombre}`)}
            testid={`${testidPrefix}-importar-tratamientos`} icono={ClipboardPaste}>Importar tratamientos</BotonAnadir>
        </Botonera>
      )}
      {['cirugias', 'tratamientos'].map((seccion) => sitio[seccion].length > 0 && (
        <div key={seccion} className="mt-2" data-testid={`${testidPrefix}-${seccion}`}>
          <h5 className="text-xs font-semibold uppercase tracking-wide text-[#1E4A3F] mb-1">
            {SECCIONES_PARTIDAS[seccion].titulo}
          </h5>
          <TablaPartidas filas={sitio[seccion]} seccion={seccion} admin={admin} acc={acc} testid="catalogo-partidas" />
        </div>
      ))}
      {vacio && <p className="text-xs text-muted-foreground mt-1">Sin cirugías ni tratamientos todavía.</p>}
    </>
  );
}

function MedicoDeBloque({ medico, hermanos, indice, admin, acc }) {
  return (
    <div className="mt-4 rounded-md border border-border p-3" data-testid="catalogo-medico">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-[#13231E]">{medico.nombre}</h3>
        {admin && (
          <Acciones hermanos={hermanos} indice={indice} etiqueta={medico.nombre} onMover={acc.mover}
            onEditar={() => acc.editar('medico', medico)} onBaja={() => acc.baja('medico', medico)} />
        )}
      </div>
      <Nota texto={medico.nota} className="mt-1 text-xs text-muted-foreground" />
      <PartidasDeSitio sitio={medico} admin={admin} acc={acc} testidPrefix="catalogo-medico" />
    </div>
  );
}

function BloquePrincipal({ bloque, hermanos, indice, admin, gestionaBloques, acc, abierto, onToggle }) {
  const n = cuentaBloque(bloque);
  const m = bloque.medicos.length;
  const resumen = `${cuantos(n, 'fila', 'filas')}${bloque.lista_unica ? ' · lista única' : ` · ${cuantos(m, 'médico', 'médicos')}`}`;
  return (
    <Desplegable principal testid="catalogo-bloque" titulo={bloque.nombre} resumen={resumen}
      abierto={abierto} onToggle={onToggle}>
      <Nota texto={bloque.nota} testid="catalogo-bloque-nota"
        className="mb-2 rounded bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5" />
      {(admin || gestionaBloques) && (
        <div className="flex flex-wrap items-start justify-between gap-2">
          <Botonera>
            {admin && !bloque.lista_unica && (
              <BotonAnadir onClick={() => acc.nuevo('medico', bloque.id, null, bloque.nombre)}
                testid="catalogo-bloque-nuevo-medico" icono={UserPlus}>Médico</BotonAnadir>
            )}
          </Botonera>
          {/* Subir y bajar, administración; el lápiz y la baja del bloque,
              solo quien tiene la casilla (el servidor da 403 al resto). */}
          <Acciones hermanos={hermanos} indice={indice} etiqueta={bloque.nombre} onMover={acc.mover}
            sinOrden={!admin} sinEdicion={!gestionaBloques}
            onEditar={() => acc.editar('bloque', bloque)} onBaja={() => acc.baja('bloque', bloque)} />
        </div>
      )}
      <PartidasDeSitio sitio={bloque} admin={admin} acc={acc} testidPrefix="catalogo-bloque" />
      {bloque.medicos.map((med, i) => (
        <MedicoDeBloque key={med.id} medico={med} hermanos={bloque.medicos} indice={i} admin={admin} acc={acc} />
      ))}
      {!bloque.lista_unica && m === 0 && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="catalogo-bloque-sin-medicos">
          Sin subapartados de médico todavía{admin ? ': añade el primero con «Médico».' : '.'}
        </p>
      )}
    </Desplegable>
  );
}

// ---------------------------------------------------------------------------
// HOSPITALES — Tarifas
// ---------------------------------------------------------------------------

const COLUMNAS_FORFAIT = [
  ['sin_iva', 'Importe sin IVA'], ['con_iva', 'Importe con IVA'],
  ['estancia_sin_iva', 'Con 1 estancia (sin IVA)'], ['estancia_con_iva', 'Con 1 estancia (con IVA)'],
];

function TablaForfaits({ hospital, admin, acc }) {
  const filas = hospital.forfaits;
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-[#13231E]">Forfaits quirúrgicos</h4>
        {admin && (
          <div className="flex flex-wrap gap-2">
            <BotonAnadir onClick={() => acc.nuevo('forfait', hospital.id, null, hospital.nombre)}
              testid="catalogo-forfaits-nuevo">Forfait</BotonAnadir>
            <BotonAnadir onClick={() => acc.importar('forfait', hospital.id, `forfaits de ${hospital.nombre}`)}
              testid="catalogo-forfaits-importar" icono={ClipboardPaste}>Importar</BotonAnadir>
          </div>
        )}
      </div>
      {filas.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-1">Sin forfaits todavía.</p>
      ) : (
        <div className="overflow-x-auto mt-1">
          <table className={`w-full text-sm table-fixed ${admin ? 'min-w-[900px]' : 'min-w-[720px]'}`} data-testid="catalogo-forfaits">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-semibold">Forfait</th>
                {COLUMNAS_FORFAIT.map(([campo, titulo]) => (
                  <th key={campo} className="py-2 px-3 font-semibold text-right w-32">{titulo}</th>
                ))}
                {admin && <th className="py-2 pl-3 w-44" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={f.id} className="border-b border-border/60" data-testid="catalogo-forfait">
                  <td className="py-1.5 pr-3 font-medium text-[#13231E]">{f.nombre}</td>
                  {COLUMNAS_FORFAIT.map(([campo]) => (
                    <td key={campo} className="py-1.5 px-3 text-right tabular-nums whitespace-nowrap">
                      {textoImporte(f[campo], f[`${campo}_matiz`])}
                    </td>
                  ))}
                  {admin && (
                    <td className="py-1 pl-3">
                      <Acciones hermanos={filas} indice={i} etiqueta={f.nombre} onMover={acc.mover}
                        onEditar={() => acc.editar('forfait', f)} onBaja={() => acc.baja('forfait', f)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GrupoServicios({ grupo, hermanos, indice, admin, acc }) {
  const filas = grupo.servicios;
  return (
    <div className="mt-4" data-testid="catalogo-grupo">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-[#13231E]">{grupo.nombre}</h4>
        {admin && (
          <div className="flex flex-wrap items-center gap-2">
            <BotonAnadir onClick={() => acc.nuevo('servicio', grupo.id, null, grupo.nombre)}
              testid="catalogo-grupo-nuevo">Servicio</BotonAnadir>
            <BotonAnadir onClick={() => acc.importar('servicio', grupo.id, grupo.nombre)}
              testid="catalogo-grupo-importar" icono={ClipboardPaste}>Importar</BotonAnadir>
            <Acciones hermanos={hermanos} indice={indice} etiqueta={grupo.nombre} onMover={acc.mover}
              onEditar={() => acc.editar('grupo_servicios', grupo)} onBaja={() => acc.baja('grupo_servicios', grupo)} />
          </div>
        )}
      </div>
      <Nota texto={grupo.nota} className="text-xs text-muted-foreground" />
      {filas.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-1">Sin servicios todavía.</p>
      ) : (
        <div className="overflow-x-auto mt-1">
          <table className={`w-full text-sm table-fixed ${admin ? 'min-w-[660px]' : 'min-w-[480px]'}`} data-testid="catalogo-servicios">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-semibold">Concepto</th>
                <th className="py-2 px-3 font-semibold text-right w-40">Importe sin IVA</th>
                <th className="py-2 px-3 font-semibold text-right w-40">Importe con IVA</th>
                {admin && <th className="py-2 pl-3 w-44" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((s, i) => (
                <tr key={s.id} className="border-b border-border/60 align-top" data-testid="catalogo-servicio">
                  <td className="py-1.5 pr-3">
                    <span className="font-medium text-[#13231E]">{s.nombre}</span>
                    {s.nota && <span className="block text-xs text-muted-foreground whitespace-pre-line">{s.nota}</span>}
                  </td>
                  <td className="py-1.5 px-3 text-right tabular-nums whitespace-nowrap">{textoImporte(s.sin_iva, s.sin_iva_matiz)}</td>
                  <td className="py-1.5 px-3 text-right tabular-nums whitespace-nowrap">{textoImporte(s.con_iva, s.con_iva_matiz)}</td>
                  {admin && (
                    <td className="py-1 pl-3">
                      <Acciones hermanos={filas} indice={i} etiqueta={s.nombre} onMover={acc.mover}
                        onEditar={() => acc.editar('servicio', s)} onBaja={() => acc.baja('servicio', s)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TarjetaHospital({ hospital, hermanos, indice, admin, acc }) {
  const [abierto, setAbierto] = useState(false);
  const tarifa = [hospital.titulo_tarifa, hospital.vigencia].filter(Boolean).join(', ');
  return (
    <Desplegable
      testid="catalogo-hospital"
      titulo={<>{hospital.nombre}{tarifa && <span className="font-normal text-muted-foreground"> · {tarifa}</span>}</>}
      resumen={`${cuantos(hospital.forfaits.length, 'forfait', 'forfaits')} · ${cuantos(hospital.grupos.length, 'grupo de servicios', 'grupos de servicios')}`}
      abierto={abierto}
      onToggle={() => setAbierto((a) => !a)}
    >
      {admin && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <BotonAnadir onClick={() => acc.nuevo('grupo_servicios', hospital.id, null, hospital.nombre)}
            testid="catalogo-hospital-nuevo-grupo" icono={FolderPlus}>Grupo de servicios</BotonAnadir>
          <Acciones hermanos={hermanos} indice={indice} etiqueta={hospital.nombre} onMover={acc.mover}
            onEditar={() => acc.editar('hospital', hospital)} onBaja={() => acc.baja('hospital', hospital)} />
        </div>
      )}
      <Nota texto={hospital.nota_cabecera} testid="catalogo-hospital-nota-cabecera"
        className="mt-2 font-semibold text-[#8a5a12]" />
      <TablaForfaits hospital={hospital} admin={admin} acc={acc} />
      {hospital.grupos.map((g, i) => (
        <GrupoServicios key={g.id} grupo={g} hermanos={hospital.grupos} indice={i} admin={admin} acc={acc} />
      ))}
      {hospital.nota_final && (
        <div className="mt-5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-950"
          data-testid="catalogo-hospital-nota-final">
          <div className="text-xs font-semibold uppercase tracking-wide mb-1">Nota importante</div>
          <p className="text-sm whitespace-pre-line">{hospital.nota_final}</p>
        </div>
      )}
    </Desplegable>
  );
}

// ---------------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------------

/** Las opciones de «Apartado» al editar un tratamiento o una cirugía. */
function apartadosDe(datos) {
  const out = [];
  datos.apartados.forEach((a) => {
    out.push({ id: a.id, etiqueta: a.nombre });
    (a.subapartados || []).forEach((s) => out.push({ id: s.id, etiqueta: `${a.nombre} ▸ ${s.nombre}` }));
  });
  return out;
}

export default function PreciosCatalogo({ admin = false, gestion = false }) {
  // El enlace del Registro de externos abre su bloque (?bloque=alquiler-externos).
  // De la barra del navegador, no de un hook del router: la página también se
  // monta sin router (las pruebas del catálogo).
  const [pideExternos] = useState(() => {
    try {
      return new URLSearchParams(window.location.search || '').get('bloque') === BLOQUE_EXTERNOS;
    } catch {
      return false;
    }
  });
  const [catalogo, setCatalogo] = useState(catalogoVacio);
  const [sistema, setSistema] = useState(SISTEMA_VACIO);
  // Las fichas de médico (`/api/surgeons`), para enlazar un bloque o un
  // médico con la suya. Solo se piden a quien puede crearlos.
  const [fichas, setFichas] = useState([]);
  const [estado, setEstado] = useState('cargando');
  const [abiertos, setAbiertos] = useState({
    tratamientos: false, cirugias: false, hospitales: false, externos: pideExternos,
  });
  const [bloquesAbiertos, setBloquesAbiertos] = useState({});
  const [dialogo, setDialogo] = useState(null);
  const [importar, setImportar] = useState(null);
  // El diálogo del tarifario de colaboradores: {} alta, {elemento} edición.
  const [dialogoColab, setDialogoColab] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get('/api/catalogo-precios'),
        api.get('/api/catalogo-precios/del-sistema').catch(() => ({ data: SISTEMA_VACIO })),
      ]);
      const cat = normalizarCatalogo(c?.data);
      setCatalogo(cat);
      const d = s?.data && typeof s.data === 'object' && !Array.isArray(s.data) ? s.data : {};
      setSistema({
        tratamientos_colaboradores: lista(d.tratamientos_colaboradores),
        cirugias_colaboradores: lista(d.cirugias_colaboradores),
      });
      setEstado('listo');
      if (admin || cat.gestiona_bloques) {
        api.get('/api/surgeons')
          .then((r) => setFichas(lista(r?.data).filter((f) => f && f.id && f.name)))
          .catch(() => setFichas([]));
      }
    } catch (e) {
      setEstado(mensajeError(e, 'no se ha podido leer'));
    }
  }, [admin]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!pideExternos || estado !== 'listo') return;
    setAbiertos((a) => ({ ...a, externos: true }));
    const el = document.querySelector('[data-testid="catalogo-externos"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pideExternos, estado]);

  const conmutar = (k) => setAbiertos((a) => ({ ...a, [k]: !a[k] }));
  const conmutarBloque = (id) => setBloquesAbiertos((a) => ({ ...a, [id]: !a[id] }));
  const gestionaBloques = catalogo.gestiona_bloques;
  const destinos = destinosDe(catalogo);

  // Lo que hacen los botones de cada fila y de cada bloque.
  const acc = {
    nuevo: (tipo, padreId, seccion, donde) => setDialogo({ tipo, padreId, seccion, donde, fichas }),
    // Los botones de arriba (28/10/2026): en uno o varios médicos o bloques
    // a la vez; sin ninguno todavía, a la lista anterior.
    nuevoArriba: (tipo) => (destinos.length
      ? setDialogo({ tipo, multiple: true, destinos })
      : setDialogo({ tipo, padreId: null, seccion: null, donde: 'sin apartado (lista anterior)' })),
    editar: (tipo, elemento) => {
      const seccion = elemento.seccion;
      const apartados = SECCIONES_PARTIDAS[seccion] ? apartadosDe(catalogo[seccion]) : null;
      setDialogo({ tipo, elemento, apartados, destinos, fichas });
    },
    importar: (tipo, padreId, titulo) => setImportar({ tipo, padreId, titulo }),
    baja: async (tipo, elemento) => {
      const cuelga = ['apartado', 'hospital', 'grupo_servicios', 'bloque', 'medico'].includes(tipo)
        ? ' Lo que tiene dentro deja de verse con él.' : '';
      // Con su artículo y concordando (6/10/2026): «¿Dar de baja el
      // tratamiento «X»?», «¿Dar de baja la cirugía «X»?» → «Dada de baja».
      if (!window.confirm(`¿Dar de baja ${conArticulo(tipo)} «${elemento.nombre}»?${cuelga}`)) return;
      try {
        await api.delete(`/api/catalogo-precios/${elemento.id}`);
        toast.success(esFemenino(tipo) ? 'Dada de baja' : 'Dado de baja');
        cargar();
      } catch (e) { toast.error(mensajeError(e, 'No se ha podido dar de baja')); }
    },
    mover: async (hermanos, i, paso) => {
      const j = i + paso;
      if (j < 0 || j >= hermanos.length) return;
      const ids = hermanos.map((h) => h.id);
      [ids[i], ids[j]] = [ids[j], ids[i]];
      try {
        await api.put('/api/catalogo-precios/orden', { ids });
        cargar();
      } catch (e) { toast.error(mensajeError(e, 'No se ha podido cambiar el orden')); }
    },
    // La migración manual (28/10/2026): una fila a un médico o a un bloque,
    // o todas las de un apartado. Nada se mueve sin pedirlo.
    moverA: async (tipo, fila, padreId) => {
      const destino = destinos.find((d) => d.id === padreId);
      try {
        await api.put(`/api/catalogo-precios/${fila.id}`, { padre_id: padreId });
        toast.success(`${esFemenino(tipo) ? 'Movida' : 'Movido'} a ${destino ? destino.etiqueta : 'su sitio'}`);
        cargar();
      } catch (e) { toast.error(mensajeError(e, 'No se ha podido mover')); }
    },
    moverTodas: async (filas, padreId, deDonde) => {
      const destino = destinos.find((d) => d.id === padreId);
      const n = filas.length;
      if (!window.confirm(`¿Mover ${n === 1 ? 'la fila' : `las ${n} filas`} de ${deDonde} a «${destino ? destino.etiqueta : ''}»?`)) return;
      try {
        await api.put('/api/catalogo-precios/mover', { ids: filas.map((f) => f.id), padre_id: padreId });
        toast.success(`${cuantos(n, 'fila movida', 'filas movidas')} a ${destino ? destino.etiqueta : 'su sitio'}`);
      } catch (e) { toast.error(mensajeError(e, 'No se han podido mover')); }
      // Con un error a medias ya hay filas movidas: la lista se pone al día.
      cargar();
    },
    // El tarifario de tratamientos de colaboradores (27/10/2026): por sus
    // rutas, no por las del catálogo de precios. Retirar es blando en el
    // servidor: deja de salir para elegir y lo ya registrado no cambia.
    colabNuevo: () => setDialogoColab({}),
    colabEditar: (elemento) => setDialogoColab({ elemento }),
    colabBaja: async (elemento) => {
      if (!window.confirm(`¿Dar de baja el tratamiento de colaboradores «${elemento.nombre}»? Deja de salir para elegir en Colaboradores; lo ya registrado no cambia.`)) return;
      try {
        await api.delete(`${RUTA_TRATAMIENTOS_COLAB}/${elemento.id}`);
        toast.success('Dado de baja');
      } catch (e) {
        toast.error(mensajeError(e, 'No se ha podido dar de baja'));
      }
      // Con 404 (lo retiró otra persona) también: la lista se pone al día.
      cargar();
    },
  };

  if (estado !== 'listo') {
    return (
      <div className="cjo-card p-4 mt-4 text-sm" data-testid="catalogo-precios-estado">
        {estado === 'cargando' ? <span className="text-muted-foreground">Cargando el catálogo…</span>
          : <span className="text-red-700" role="alert">No se ha podido leer el catálogo: {estado}.</span>}
      </div>
    );
  }

  const nT = cuentaPartidas(catalogo.tratamientos);
  const nC = cuentaPartidas(catalogo.cirugias);
  const nH = catalogo.hospitales.length;
  const nB = catalogo.bloques.length;
  return (
    <div data-testid="catalogo-precios">
      {/* Arriba a la derecha, siempre a la vista (28/10/2026): los dos
          botones de las partidas y, con la casilla, el del bloque. */}
      {(admin || gestionaBloques) && (
        <div className="sticky top-0 z-10 -mx-1 px-1 py-2 flex flex-wrap items-center justify-end gap-2 bg-background/95 backdrop-blur"
          data-testid="catalogo-botones-arriba">
          {gestionaBloques && (
            <Button type="button" size="sm" variant="outline" data-testid="catalogo-nuevo-bloque"
              onClick={() => acc.nuevo('bloque', null, null, 'bloques principales')}>
              <LayoutList className="w-3.5 h-3.5 mr-1.5" />Bloque principal
            </Button>
          )}
          {admin && (
            <>
              <Button type="button" size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="catalogo-anadir-cirugia"
                onClick={() => acc.nuevoArriba('cirugia')}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />Añadir cirugía
              </Button>
              <Button type="button" size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="catalogo-anadir-tratamiento"
                onClick={() => acc.nuevoArriba('tratamiento')}>
                <Plus className="w-3.5 h-3.5 mr-1.5" />Añadir tratamiento
              </Button>
            </>
          )}
        </div>
      )}

      {catalogo.bloques.map((b, i) => (
        <BloquePrincipal key={b.id} bloque={b} hermanos={catalogo.bloques} indice={i} admin={admin}
          gestionaBloques={gestionaBloques} acc={acc} abierto={!!bloquesAbiertos[b.id]}
          onToggle={() => conmutarBloque(b.id)} />
      ))}
      {nB === 0 && (admin || gestionaBloques) && (
        <p className="text-xs text-muted-foreground mt-4 px-1" data-testid="catalogo-sin-bloques">
          {gestionaBloques
            ? 'Aún no hay bloques principales: crea el primero con «Bloque principal» (p. ej. el del médico titular, como lista única, y otro con sus médicos) y luego reparte la lista anterior.'
            : 'Aún no hay bloques principales por médico. Los crea quien tiene «Gestiona los bloques del Catálogo» en Usuarios; mientras, las cirugías y los tratamientos van a la lista de abajo.'}
        </p>
      )}

      {['tratamientos', 'cirugias'].map((seccion) => {
        const n = seccion === 'tratamientos' ? nT : nC;
        const nA = catalogo[seccion].apartados.length;
        return (
          <Desplegable key={seccion} principal testid={`catalogo-${seccion}`}
            titulo={nB > 0 ? `${SECCIONES_PARTIDAS[seccion].titulo} — lista anterior` : SECCIONES_PARTIDAS[seccion].titulo}
            resumen={`${n} ${n === 1 ? 'fila' : 'filas'}${nB > 0 && n > 0 ? ' sin asignar' : ''}${nA ? ` · ${nA} ${nA === 1 ? 'apartado' : 'apartados'}` : ''}`}
            abierto={abiertos[seccion]} onToggle={() => conmutar(seccion)}>
            <SeccionPartidas seccion={seccion} datos={catalogo[seccion]} admin={admin} gestion={gestion}
              acc={acc} sistema={sistema} destinos={destinos} />
          </Desplegable>
        );
      })}
      <Desplegable principal testid="catalogo-hospitales" titulo="HOSPITALES — Tarifas"
        resumen={`${nH} ${nH === 1 ? 'hospital' : 'hospitales'}`}
        abierto={abiertos.hospitales} onToggle={() => conmutar('hospitales')}>
        {admin && (
          <Botonera>
            <BotonAnadir onClick={() => acc.nuevo('hospital', null, null, 'hospitales')}
              testid="catalogo-hospitales-nuevo">Hospital</BotonAnadir>
          </Botonera>
        )}
        {nH === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="catalogo-hospitales-vacio">
            Aún no hay tarifas de hospital{admin ? ': añade el primer hospital y luego pega sus forfaits y servicios.' : '.'}
          </p>
        )}
        {catalogo.hospitales.map((h, i) => (
          <TarjetaHospital key={h.id} hospital={h} hermanos={catalogo.hospitales} indice={i} admin={admin} acc={acc} />
        ))}
      </Desplegable>

      {/* Las tarifas del Registro de externos (23/10/2026): las consulta todo el
          que entra aquí y las edita solo quien tiene la casilla en Usuarios. */}
      <AlquilerQuirofanoExternos Desplegable={Desplegable} abierto={abiertos.externos}
        onToggle={() => conmutar('externos')} />

      <ElementoPrecioDialog destino={dialogo} onClose={() => setDialogo(null)} onGuardado={cargar} />
      <TratamientoColaboradorDialog destino={dialogoColab} onClose={() => setDialogoColab(null)} onGuardado={cargar} />
      <ImportarPreciosDialog abierto={!!importar} tipo={importar?.tipo} padreId={importar?.padreId}
        titulo={importar?.titulo} onClose={() => setImportar(null)} onGuardado={cargar} />
    </div>
  );
}
